import React, { useState } from "react";
import { isEmpty } from "lodash";
import classNames from "classnames";
import { Collapse } from "@mui/material";

import { useStateSelector } from "utils/field";
import { getStoreyNameFromId } from "utils/singleZoneCooling";
import { flattenComponents } from "utils/enclosure/components";
import { ZoneWarningTooltip } from "../../ZoneTooltips";

import ArrowIcon from "assets/images/icons/arrow-down.svg";
import WallIcon from "assets/images/components/Wall.svg";
import ExpFloorIcon from "assets/images/components/ExpFloor.svg";
import CeilingIcon from "assets/images/components/Ceiling.svg";
import BasementIcon from "assets/images/components/Foundation-Basement.svg";
import CrawlspaceIcon from "assets/images/components/Foundation-Crawlspace.svg";
import SlabIcon from "assets/images/components/Foundation-Slab.svg";
import WindowIcon from "assets/images/components/Window.svg";
import DoorIcon from "assets/images/components/Door.svg";
import FloorHeaderIcon from "assets/images/components/FloorHeader.svg";

import classes from "./styles.module.scss";
import zoneResultsClasses from "../styles.module.scss";

const COMPONENT_ICONS = {
    wall: WallIcon,
    expFloor: ExpFloorIcon,
    ceiling: CeilingIcon,
    basement: BasementIcon,
    crawlspace: CrawlspaceIcon,
    slab: SlabIcon,
    window: WindowIcon,
    door: DoorIcon,
    floorHeader: FloorHeaderIcon,
};

const getComponentIcon = (component = {}) =>
    COMPONENT_ICONS[component.componentType] || "";

const resolveComponent = (component = {}, componentsById = {}) => {
    const compId = component.compId.split("_").pop(); 
    const componentData = componentsById[compId] || {};

    return {
        ...componentData,
        ...component,
        label: component.label || "",
        componentType: componentData?.componentType || "",
        warning: isEmpty(componentData) ? "missingComponent" : "",
    };
};

const BreakdownSection = ({
    title = "",
    items = [],
    getLabel = () => "",
    getKey = (_, index) => index,
    getIcon,
    emptyText = "None to display.",
}) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className={classNames(classes.section, { [classes.isExpanded]: expanded })}>
            <div className={classes.sectionHeader} onClick={() => setExpanded(!expanded)}>
                <p>{title} ({items.length})</p>
                <div className={classes.expandButton}>
                    <img src={ArrowIcon} alt="expand" />
                </div>
            </div>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={classes.sectionBody}>
                    {isEmpty(items) && <div className={classes.emptyNotice}>{emptyText}</div>}
                    {items.map((item, index) => {
                        const icon = getIcon?.(item);
                        const key = getKey(item, index);
                        const label = getLabel(item);
                        const warning = item?.warning;

                        return (
                            <div key={key} className={classes.itemRow}>
                                <div className={classes.itemIndex}>
                                    {index + 1}
                                </div>
                                {icon && <img src={icon} alt="" />}
                                {warning && <ZoneWarningTooltip id={`missing-${key}`} warning={warning} />}
                                <p>{label}</p>
                            </div>
                        );
                    })}
                </div>
            </Collapse>
        </div>
    );
};

const ZoneRoomBreakdown = ({
    selectedStorey = "",
    roomLabels = [],
    exteriorComponents = [],
    interiorComponents = [],
}) => {
    const { components = {}, intComponents = {}, specifications = {} } = useStateSelector(`model.modelData`) || {};
    const { id: storeysId } = specifications.numStoreys || {};

    const extComponentData = flattenComponents(components);
    const intComponentData = flattenComponents(intComponents);

    const resolvedExteriorComponents = exteriorComponents.map((component) => resolveComponent(component, extComponentData));
        //.filter((component) => !["floorHeader", "window", "door"].includes(component.componentType));
    const resolvedInteriorComponents = interiorComponents.map((component) => resolveComponent(component, intComponentData));
        //.filter((component) => !["floorHeader", "window", "door"].includes(component.componentType));

    return (
        <div className={zoneResultsClasses.sectionContainer}>
            <div className={zoneResultsClasses.sectionHeader}>
                <h3 className={zoneResultsClasses.sectionTitle}>
                    Designated Rooms & Components
                </h3>
                <div className={zoneResultsClasses.resultItem}>
                    <p className={zoneResultsClasses.resultItemTitle}>Location:</p>
                    <p>{getStoreyNameFromId(storeysId, selectedStorey)}</p>
                </div>
            </div>
            <div className={classes.sectionContent}>
                <BreakdownSection
                    title="Designated Rooms"
                    items={roomLabels}
                    getKey={(room) => room.roomId}
                    getLabel={(room) => room.label}
                    emptyText="No rooms to display."
                />
                <BreakdownSection
                    title="Exterior Components"
                    items={resolvedExteriorComponents}
                    getKey={(component) => component.compId}
                    getLabel={(component) => component.label}
                    getIcon={getComponentIcon}
                    emptyText="No components to display."
                />
                <BreakdownSection
                    title="Interior Components"
                    items={resolvedInteriorComponents}
                    getKey={(component) => component.compId}
                    getLabel={(component) => component.label}
                    getIcon={getComponentIcon}
                    emptyText="No components to display."
                />
            </div>
        </div>
    );
};

export default ZoneRoomBreakdown;
